import { onBeforeUnmount, shallowRef } from "vue"

/** Copies the original stylesheet text: Chrome's re-serialized cssText drops var() border shorthands, breaking custom scrollbars. */
function copyStyles(target: Document) {
  Array.from(document.styleSheets).forEach(styleSheet => {
    const sourceText = styleSheet.ownerNode?.textContent
    if (sourceText) {
      const style = target.createElement("style")
      style.textContent = sourceText
      target.head.appendChild(style)
      return
    }
    if (styleSheet.href) {
      const link = target.createElement("link")
      link.rel = "stylesheet"
      link.href = styleSheet.href
      target.head.appendChild(link)
      return
    }
    try {
      const style = target.createElement("style")
      style.textContent = Array.from(styleSheet.cssRules)
        .map(rule => rule.cssText)
        .join("")
      target.head.appendChild(style)
    } catch {
      return
    }
  })
  target.documentElement.className = document.documentElement.className
  target.body.className = document.body.className
}

/** Opens an always-on-top Document Picture-in-Picture window and exposes its root element as a Teleport target. Chromium only. */
export function useDocumentPictureInPicture() {
  const isSupported = "documentPictureInPicture" in window
  const container = shallowRef<HTMLElement | null>(null)
  const isOpening = shallowRef(false)
  let openRequestId = 0

  /** Resolves to false only when the browser refuses the window, e.g. without a user gesture. */
  async function open({
    width,
    height,
    className,
    minWidth = 0,
    minHeight = 0
  }: {
    width: number
    height: number
    className: string
    minWidth?: number
    minHeight?: number
  }) {
    if (!window.documentPictureInPicture) return false
    if (container.value || isOpening.value) return true

    const requestId = ++openRequestId
    let newWindow: Window
    isOpening.value = true
    try {
      newWindow = await window.documentPictureInPicture.requestWindow({
        width: Math.max(width, minWidth),
        height: Math.max(height, minHeight),
        disallowReturnToOpener: true
      })
    } catch {
      return false
    } finally {
      if (requestId === openRequestId) isOpening.value = false
    }
    if (requestId !== openRequestId) {
      newWindow.close()
      return true
    }
    copyStyles(newWindow.document)

    const element = newWindow.document.createElement("div")
    element.className = className
    element.style.minWidth = `${minWidth}px`
    element.style.minHeight = `${minHeight}px`
    newWindow.document.body.appendChild(element)

    newWindow.addEventListener("pagehide", () => {
      if (container.value !== element) return
      container.value = null
    })

    container.value = element
    return true
  }

  /** Releases the container right away, since pagehide arrives asynchronously and would block an immediate reopen; also cancels a pending open. */
  function close() {
    openRequestId++
    isOpening.value = false
    const pipWindow = container.value?.ownerDocument.defaultView
    container.value = null
    pipWindow?.close()
  }

  onBeforeUnmount(close)

  return { isSupported, container, isOpening, open, close }
}
